<template>
  <div class="spring-overlay" v-if="visible">
    <div class="top-line"></div>

    <div class="hanging-decor left-decor">
      <div class="string"></div>
      <div class="lantern-box">
        <div class="lantern">🏮</div>
        <div class="tassel-tag">
          <span v-for="(char, index) in leftLanternChars" :key="index">{{ char }}</span>
        </div>
      </div>
    </div>

    <div class="hanging-decor right-decor">
      <div class="string short"></div>
      <div class="lantern-box">
        <div class="lantern">🏮</div>
        <div class="tassel-tag">
          <span v-for="(char, index) in rightLanternChars" :key="index">{{ char }}</span>
        </div>
      </div>
    </div>

    <div class="floating-container">
      <span v-for="n in 6" :key="n" class="floater" :style="getFloaterStyle(n)">
        {{ getIcon(n) }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'

const props = defineProps({
  newYearConfig: {
    type: Object,
    default: null
  }
})

const visible = ref(false)
const icons = ['✨', '🧧', '🌸', '✨', '🧧', '🍂']

const getIcon = (n) => icons[n % icons.length]

// 左侧灯笼文字（接口配置或默认）
const leftLanternChars = computed(() => {
  const text = props.newYearConfig?.leftLantern || '新春快乐'
  return text.split('')
})

// 右侧灯笼文字（接口配置或默认）
const rightLanternChars = computed(() => {
  const text = props.newYearConfig?.rightLantern || '马到成功'
  return text.split('')
})

// 随机生成漂浮物的位置和速度
const getFloaterStyle = (n) => {
  return {
    left: `${Math.random() * 90 + 5}%`, // 5% ~ 95%
    animationDuration: `${10 + Math.random() * 10}s`, // 10-20s 慢速飘落
    animationDelay: `${Math.random() * 5}s`,
    fontSize: `${14 + Math.random() * 10}px`
  }
}

// 检查时间逻辑
const checkShowDecorations = () => {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  
  let startDate, endDate
  
  if (props.newYearConfig) {
    // 使用接口返回的时间配置
    startDate = new Date(props.newYearConfig.startDate)
    endDate = new Date(props.newYearConfig.endDate)
  } else {
    // 使用默认时间配置（2026年春节）
    startDate = new Date('2026-02-14')
    endDate = new Date('2026-02-28')
  }
  
  startDate.setHours(0, 0, 0, 0)
  endDate.setHours(0, 0, 0, 0)

  if (now >= startDate && now <= endDate) {
    visible.value = true
  }
}

watch(() => props.newYearConfig, () => {
  if (props.newYearConfig) {
    checkShowDecorations()
  }
}, { immediate: true })

onMounted(() => {
  // 如果没有配置，等待一下再检查
  if (!props.newYearConfig) {
    setTimeout(() => {
      checkShowDecorations()
    }, 500)
  }
})
</script>

<style scoped>
/* 核心容器：全屏覆盖，但允许点击穿透 */
.spring-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  pointer-events: none;
  /* 关键：让鼠标事件穿透，不影响下方导航栏点击 */
  overflow: hidden;
}

/* 1. 顶部金线 */
.top-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, #c41e3a, #ffd700, #c41e3a);
  box-shadow: 0 0 10px rgba(196, 30, 58, 0.5);
  z-index: 10000;
}

/* 2. 悬挂装饰通用样式 */
.hanging-decor {
  position: absolute;
  top: -10px;
  /* 稍微往上藏一点绳子头 */
  display: flex;
  flex-direction: column;
  align-items: center;
  filter: drop-shadow(2px 4px 6px rgba(0, 0, 0, 0.3));
  transform-origin: top center;
  animation: swing 6s ease-in-out infinite;
}

.left-decor {
  left: 40px;
  /* 避开左侧Logo区域 */
  animation-delay: 0s;
}

.right-decor {
  right: 40px;
  /* 避开右侧用户区域 */
  animation-delay: -3s;
  /* 错开摆动节奏 */
}

/* 绳子 */
.string {
  width: 2px;
  height: 60px;
  background: #c41e3a;
}

.string.short {
  height: 45px;
  /* 让左右高低错落 */
}

/* 灯笼主体 */
.lantern-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: grab;
  pointer-events: auto;
  /* 只有灯笼本身可以交互(比如hover效果) */
  transition: transform 0.3s;
}

.lantern-box:hover {
  transform: scale(1.1);
}

.lantern {
  font-size: 38px;
  line-height: 1;
  margin-bottom: -5px;
  z-index: 2;
}

/* 竖排文字标签 (类似流苏上的挂牌) */
.tassel-tag {
  background: #b71c1c;
  color: #ffd700;
  padding: 8px 4px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: bold;
  writing-mode: vertical-rl;
  /* 竖排文字 */
  text-orientation: upright;
  letter-spacing: 2px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  border: 1px solid #ffd700;
  margin-top: -5px;
  z-index: 1;
}

/* 摆动动画 */
@keyframes swing {

  0%,
  100% {
    transform: rotate(-3deg);
  }

  50% {
    transform: rotate(3deg);
  }
}

/* 3. 漂浮背景 */
.floating-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.floater {
  position: absolute;
  top: -10%;
  opacity: 0.6;
  animation: fall linear infinite;
}

@keyframes fall {
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 0;
  }

  20% {
    opacity: 0.8;
  }

  100% {
    transform: translateY(110vh) rotate(360deg);
    opacity: 0;
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .left-decor {
    left: 10px;
  }

  .right-decor {
    right: 10px;
  }

  .lantern {
    font-size: 28px;
  }

  .tassel-tag {
    font-size: 12px;
    padding: 4px 2px;
  }
}
</style>